window.PXUTheme = window.PXUTheme || {};

(function(ns) {
  // Prevent redeclaration if loaded twice
  if (ns.SmartGallery) return;

  // SmartGallery class scoped under PXUTheme
  ns.SmartGallery = class {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.imageParent = wrapper.querySelector(".smart-gallery");
      this.inactiveGallery = wrapper.querySelector(".smart-gallery-inactive");
      this.thumbsNav = wrapper.querySelector(".smart-gallery__thumbs-nav");
      this.thumbsEl = wrapper.querySelector(".smart-gallery__thumbs");
      this.thumbsPrev = wrapper.querySelector(".smart-gallery__thumbs-arrow--prev");
      this.thumbsNext = wrapper.querySelector(".smart-gallery__thumbs-arrow--next");
      this.flickityInstance = null;
      this.selectedVariant = this.imageParent?.dataset.selectedVariant || "";
      this.shareUntaggedImages = wrapper.dataset.shareUntaggedImages !== "false";
      this.matchByFilename = wrapper.dataset.matchGalleryByFilename === "true";
      this.colourHandles = (wrapper.dataset.colourHandles || "")
        .split("|||")
        .map((h) => h.trim().toLowerCase())
        .filter(Boolean);
      this._onResize = this.handleResize.bind(this);
      this._onFlickitySelect = this.updateSelectedThumb.bind(this);
      this._onThumbsScroll = this.updateThumbsArrows.bind(this);
    }

    _filenameMatchesHandle(filename, handle) {
      if (!filename || !handle) return false;
      return String(filename).toLowerCase().includes(String(handle).toLowerCase());
    }

    _isFilenameBoundToAnyColour(filename) {
      if (!this.matchByFilename || !filename || !this.colourHandles.length) {
        return false;
      }
      return this.colourHandles.some((handle) =>
        this._filenameMatchesHandle(filename, handle)
      );
    }

    _mediaMatchesVariant(cell, smartImg, newVariant) {
      const altText = smartImg?.getAttribute("alt") || "";
      const filename =
        cell?.dataset?.mediaFilename ||
        smartImg?.dataset?.mediaFilename ||
        "";
      const matchesAlt = altText === `xs:${newVariant}`;
      const matchesFile =
        this.matchByFilename &&
        this._filenameMatchesHandle(filename, newVariant);
      const isAltUntagged = !altText.startsWith("xs:") || altText === "xs:";
      const isFilenameBound = this._isFilenameBoundToAnyColour(filename);
      const isUntagged = isAltUntagged && !isFilenameBound;
      const isDumbMode =
        newVariant === "false" || newVariant === "default-title";

      return {
        shouldInclude:
          !!smartImg &&
          (isDumbMode ||
            matchesAlt ||
            matchesFile ||
            (this.shareUntaggedImages && isUntagged)),
        altText,
        filename,
      };
    }

    init() {
      if (!this.imageParent || !this.inactiveGallery) {
        console.error("Smart Gallery containers not found in", this.wrapper);
        return;
      }
      if (this.wrapper.querySelector(".smart-gallery__limiter")) {
        this.watchLimiters();
      }
      this.watchSwappers();
      // Images are now pre-loaded, just initialize Flickity if needed
      this.initializeFlickityIfNeeded();
      this.syncThumbnails();
      this.bindThumbsNav();
      this.watchGallerySwatches();
      this.watchVideoActions();
      // Show NEW badge / sticker on initial variant
      this.updateGalleryBadges(this.selectedVariant);
      this.lockGalleryHeight();
      window.addEventListener("resize", this._onResize);
    }

    lockGalleryHeight() {
      // Only lock height if we're not in a carousel/mobile view
      if (window.innerWidth >= 768 && !this.imageParent.classList.contains("carousel") && !this.imageParent.classList.contains("modal")) {
        const h = this.imageParent.offsetHeight;
        if (h > 0) {
          this.imageParent.style.minHeight = `${h}px`;
        }
      } else {
        // Clear height lock for mobile/carousel views
        this.imageParent.style.minHeight = '';
      }
    }

    watchLimiters() {
      this.wrapper.querySelectorAll(".smart-gallery__limiter").forEach(limiter => {
        limiter.addEventListener("click", () => {
          this.imageParent.classList.add("unlimited");
          limiter.classList.add("hidden");
        });
      });
    }

    watchSwappers() {
      document.querySelectorAll("[data-swapper]").forEach(swapper => {
        const handler = (event) => {
          const newVariant = event.target.dataset.swapper;
          
          // Update bundle option title if present
          const bundleOption = swapper.closest(".product-bundle__option");
          if (bundleOption) {
            const titleEl = bundleOption.querySelector("[data-selected-colour]");
            if (titleEl) titleEl.textContent = event.target.dataset.title;
          }
          // Update select-colour display
          const selectColour = document.querySelector('[data-select-colour] b');
          if (selectColour) {
            selectColour.textContent = event.target.dataset.colour;
          }
          
          // Update gallery swatches to match the selected variant
          const matchingGallerySwatch = this.wrapper.querySelector(`.gallery-swatch[data-swapper="${newVariant}"]`);
          if (matchingGallerySwatch) {
            const activeSwatch = this.wrapper.querySelector(".gallery-swatch.active");
            if (activeSwatch) activeSwatch.classList.remove("active");
            matchingGallerySwatch.classList.add("active");
          }

          // Check if gallery is in "dumb" mode (showing all images, no filtering)
          const initialVariant = this.imageParent?.dataset.selectedVariant;
          const isDumbGallery = initialVariant === "false" || initialVariant === "default-title";
          
          // Don't update gallery state or images if in dumb mode
          if (!isDumbGallery) {
            this.selectedVariant = newVariant;
            this.imageParent.dataset.selectedVariant = newVariant;

            if(newVariant === "false" || newVariant === "default-title") {
              return;
            }
            this.updateActiveImages(newVariant);
          }
          
          // Dispatch custom event for variant selection
          document.dispatchEvent(new CustomEvent('variantColorSelected', {
            detail: {
              variant: newVariant,
              color: event.target.dataset.colour,
              title: event.target.dataset.title,
              source: 'swapper'
            }
          }));
        };
        swapper.addEventListener("change", handler);
        swapper.addEventListener("click", handler);
      });
    }

    watchGallerySwatches() {
      this.wrapper.querySelectorAll(".gallery-swatches .gallery-swatch").forEach(swatch => {
        swatch.addEventListener("click", () => {
          // Check if gallery is in "dumb" mode (showing all images, no filtering)
          const initialVariant = this.imageParent?.dataset.selectedVariant;
          const isDumbGallery = initialVariant === "false" || initialVariant === "default-title";
          
          // If in dumb mode, don't allow swatch interaction
          if (isDumbGallery) {
            return;
          }
          
          const active = this.wrapper.querySelector(".gallery-swatch.active");
          if (active) active.classList.remove("active");
          swatch.classList.add("active");

          // Sync variant input - try multiple methods to find the input
          const swapperValue = swatch.dataset.swapper;
          let input = null;
          
          // Method 1: Try the standard ID pattern (for product-options.liquid)
          input = document.getElementById(`input-swatch--${swapperValue}`);
          
          // Method 2: Try to find by data-swapper attribute (for variant-options.liquid)
          if (!input) {
            const inputsWithSwapper = document.querySelectorAll(`input[data-swapper="${swapperValue}"]`);
            // Find the first checked one or the first available one
            input = Array.from(inputsWithSwapper).find(inp => inp.checked) || inputsWithSwapper[0];
          }
          
          if (input) {
            input.checked = true;
            
            // Update sticky selection attributes
            const fieldset = input.closest("fieldset");
            const optionGroup = input.closest(".product_variant_option");
            
            if (fieldset) {
              fieldset.dataset.stickySelection = "selected";
            }
            if (optionGroup) {
              optionGroup.setAttribute('data-sticky-selection', 'selected');
              optionGroup.setAttribute('data-selection', 'selected');
            }
            
            // Dispatch change event to trigger all the preselection logic
            input.dispatchEvent(new Event('change', { bubbles: true }));
            
            if (window.PXUTheme.jsGlobalProduct?.updateFloatingATC) {
              window.PXUTheme.jsGlobalProduct.updateFloatingATC();
            }
          }

          // Update all selected color display elements that match this swapper value
          const selectionEls = document.querySelectorAll(`[data-selected-colour][data-option-index]`);
          selectionEls.forEach(sel => {
            const optionGroup = sel.closest('.product_variant_option');
            if (optionGroup) {
              const matchingInput = optionGroup.querySelector(`input[data-swapper="${swapperValue}"]:checked`);
              if (matchingInput && matchingInput.dataset.title) {
                sel.textContent = matchingInput.dataset.title;
              }
            }
          });
          
          // Also update the legacy single selection element (for backwards compatibility)
          const legacySelectionEl = document.querySelector(".swatch__options-selection:not([data-option-index])");
          if (legacySelectionEl) {
            legacySelectionEl.textContent = swatch.dataset.colour;
          }
          
          // Dispatch custom event for variant selection
          document.dispatchEvent(new CustomEvent('variantColorSelected', {
            detail: {
              variant: swatch.dataset.swapper,
              color: swatch.dataset.colour,
              source: 'gallerySwatch'
            }
          }));
        });
      });
    }

    handleResize() {
      // Clear any existing height lock during resize
      this.imageParent.style.minHeight = '';
      
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Reinitialize Flickity if needed
        this.initializeFlickityIfNeeded();
        // Update the locked height for the new viewport
        this.lockGalleryHeight();
        this.updateThumbsArrows();
      });
    }

    bindThumbsNav() {
      if (!this.thumbsEl || !this.thumbsPrev || !this.thumbsNext) return;

      this.thumbsPrev.addEventListener("click", () => this.scrollThumbs("prev"));
      this.thumbsNext.addEventListener("click", () => this.scrollThumbs("next"));
      this.thumbsEl.addEventListener("scroll", this._onThumbsScroll, { passive: true });
    }

    getThumbs() {
      if (!this.thumbsEl) return [];
      return Array.from(this.thumbsEl.querySelectorAll(".smart-gallery__thumb"));
    }

    getFirstVisibleThumbIndex() {
      const thumbs = this.getThumbs();
      if (!thumbs.length) return 0;

      const scrollLeft = this.thumbsEl.scrollLeft;
      for (let i = 0; i < thumbs.length; i++) {
        // Treat a thumb as the lead once its left edge is at/past the scroll position.
        if (thumbs[i].offsetLeft >= scrollLeft - 2) return i;
      }
      return thumbs.length - 1;
    }

    scrollThumbs(direction) {
      if (!this.thumbsEl) return;
      const thumbs = this.getThumbs();
      if (!thumbs.length) return;

      const firstVisible = this.getFirstVisibleThumbIndex();
      const targetIndex =
        direction === "next"
          ? Math.min(thumbs.length - 1, firstVisible + 2)
          : Math.max(0, firstVisible - 2);

      const maxScroll = Math.max(0, this.thumbsEl.scrollWidth - this.thumbsEl.clientWidth);
      // Snap to the real thumb offset so gap / subpixels stay consistent.
      const targetLeft = Math.min(maxScroll, Math.max(0, thumbs[targetIndex].offsetLeft));

      this.thumbsEl.scrollTo({
        left: targetLeft,
        behavior: "smooth",
      });
    }

    updateThumbsArrows() {
      if (!this.thumbsNav || !this.thumbsEl || !this.thumbsPrev || !this.thumbsNext) return;

      const maxScroll = this.thumbsEl.scrollWidth - this.thumbsEl.clientWidth;
      const overflowing = maxScroll > 2;
      this.thumbsNav.classList.toggle("is-overflowing", overflowing);

      if (!overflowing) {
        this.thumbsPrev.disabled = true;
        this.thumbsNext.disabled = true;
        return;
      }

      const left = this.thumbsEl.scrollLeft;
      this.thumbsPrev.disabled = left <= 2;
      this.thumbsNext.disabled = left >= maxScroll - 2;
    }

    initializeFlickityIfNeeded() {
      if (window.innerWidth < 768 || this.imageParent.classList.contains("carousel") || this.imageParent.classList.contains("modal")) {
        if (!this.flickityInstance) {
          const showDots = this.imageParent.classList.contains("dots") && !this.thumbsEl;
          this.flickityInstance = new Flickity(this.imageParent, {
            cellAlign: "left",
            contain: true,
            pageDots: showDots,
            prevNextButtons: true,
            wrapAround: true,
            imagesLoaded: true,
          });
          this.flickityInstance.on("select", this._onFlickitySelect);
          this.updateSelectedThumb();
        }
      } else if (this.flickityInstance) {
        this.flickityInstance.off("select", this._onFlickitySelect);
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }
    }

    syncThumbnails() {
      if (!this.thumbsEl) return;

      const cells = this.imageParent.querySelectorAll(".gallery-cell");
      this.thumbsEl.innerHTML = "";

      if (cells.length <= 1) {
        if (this.thumbsNav) this.thumbsNav.hidden = true;
        this.thumbsEl.hidden = true;
        return;
      }

      if (this.thumbsNav) this.thumbsNav.hidden = false;
      this.thumbsEl.hidden = false;
      const selectedIndex = this.flickityInstance ? this.flickityInstance.selectedIndex : 0;

      cells.forEach((cell, index) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "smart-gallery__thumb" + (index === selectedIndex ? " is-selected" : "");
        btn.setAttribute("role", "tab");
        btn.setAttribute("aria-label", `View image ${index + 1}`);
        btn.setAttribute("aria-selected", index === selectedIndex ? "true" : "false");

        const img = cell.querySelector("img.smart-gallery-img, img");
        const video = cell.querySelector("video");

        if (img) {
          const thumbImg = document.createElement("img");
          thumbImg.src = img.currentSrc || img.src;
          thumbImg.alt = "";
          thumbImg.loading = "lazy";
          thumbImg.decoding = "async";
          btn.appendChild(thumbImg);
        } else if (video) {
          const thumbImg = document.createElement("img");
          thumbImg.src = video.getAttribute("poster") || video.currentSrc || video.src || "";
          thumbImg.alt = "";
          thumbImg.loading = "lazy";
          if (!thumbImg.src) {
            btn.classList.add("smart-gallery__thumb--video");
          } else {
            btn.appendChild(thumbImg);
          }
        }

        btn.addEventListener("click", () => {
          if (this.flickityInstance) {
            this.flickityInstance.select(index);
          }
        });

        this.thumbsEl.appendChild(btn);
      });

      requestAnimationFrame(() => this.updateThumbsArrows());
    }

    updateSelectedThumb() {
      if (!this.thumbsEl || !this.flickityInstance) return;
      const selectedIndex = this.flickityInstance.selectedIndex;
      const thumbs = this.thumbsEl.querySelectorAll(".smart-gallery__thumb");
      thumbs.forEach((thumb, index) => {
        const isSelected = index === selectedIndex;
        thumb.classList.toggle("is-selected", isSelected);
        thumb.setAttribute("aria-selected", isSelected ? "true" : "false");
      });

      const selectedThumb = thumbs[selectedIndex];
      if (selectedThumb) {
        // Keep the selected thumb in view without centering (avoids cutting off neighbors).
        const container = this.thumbsEl;
        const thumbLeft = selectedThumb.offsetLeft;
        const thumbRight = thumbLeft + selectedThumb.offsetWidth;
        const viewLeft = container.scrollLeft;
        const viewRight = viewLeft + container.clientWidth;
        let targetLeft = null;

        if (thumbLeft < viewLeft + 2) {
          targetLeft = thumbLeft;
        } else if (thumbRight > viewRight - 2) {
          // Align so the selected thumb is the rightmost fully visible card.
          const leadIndex = Math.max(0, selectedIndex - 7);
          targetLeft = thumbs[leadIndex] ? thumbs[leadIndex].offsetLeft : thumbRight - container.clientWidth;
        }

        if (targetLeft !== null) {
          const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
          container.scrollTo({
            left: Math.min(maxScroll, Math.max(0, targetLeft)),
            behavior: "smooth",
          });
        }

        requestAnimationFrame(() => this.updateThumbsArrows());
      }
    }

    updateActiveImages(newVariant = this.selectedVariant) {
      if (this.flickityInstance) {
        this.flickityInstance.off("select", this._onFlickitySelect);
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }

      // Clear current images and rebuild from inactive gallery
      this.imageParent.innerHTML = "";

      const matchingCells = [];
      const inactiveCells = this.inactiveGallery.querySelectorAll("div.gallery-cell");
      
      inactiveCells.forEach(cell => {
        const anchor = cell.querySelector("a");
        const smartImg = anchor?.querySelector("smartimg");
        const { shouldInclude } = this._mediaMatchesVariant(
          cell,
          smartImg,
          newVariant
        );

        if (shouldInclude) {
          const clonedCell = cell.cloneNode(true);
          const clonedAnchor = clonedCell.querySelector("a");
          const clonedSmartImg = clonedAnchor?.querySelector("smartimg");
          if (clonedSmartImg) {
            const video = clonedSmartImg.querySelector("video");
            if (video) {
              const pauseIcon = clonedSmartImg.querySelector(".gicon--pause");
              const videoContainer = document.createElement("div");
              videoContainer.classList.add("video-wrapper");
              videoContainer.appendChild(video);
              if (pauseIcon) videoContainer.appendChild(pauseIcon.cloneNode(true));
              clonedSmartImg.replaceWith(videoContainer);
            } else {
              const img = document.createElement("img");
              this.copyAttributes(clonedSmartImg, img);
              // Add lazy loading for dynamically loaded images
              img.loading = "lazy";
              img.decoding = "async";
              clonedSmartImg.replaceWith(img);
            }
          }
          clonedAnchor.classList.remove("hidden");
          matchingCells.push(clonedCell);
        }
      });

      // Add matching images
      const fragment = document.createDocumentFragment();
      matchingCells.forEach(cell => fragment.appendChild(cell));
      this.imageParent.appendChild(fragment);

      // Handle NEW badge and sticker on first image
      this.updateGalleryBadges(newVariant);

      this.initializeFlickityIfNeeded();
      this.syncThumbnails();
      this.watchVideoActions();
    }

    updateGalleryBadges(variant) {
      const cells = this.imageParent.querySelectorAll(".gallery-cell");
      const newColoursStr = this.wrapper.dataset.newColours || '';
      const newColours = newColoursStr ? newColoursStr.split('|||').map(c => c.trim()).filter(Boolean) : [];
      const badgeText = this.wrapper.dataset.newBadgeText || 'NEW';
      const hasSticker = this.wrapper.dataset.hasSticker === 'true';
      const isNewVariant = newColours.includes(variant);
      let badgeShown = false;

      cells.forEach(cell => {
        // Remove any existing featured_tag or sticker
        const existingTag = cell.querySelector('.featured_tag');
        if (existingTag) existingTag.remove();
        const existingSticker = cell.querySelector('.sticker');
        if (existingSticker) existingSticker.remove();
      });

      cells.forEach((cell, index) => {
        if (index !== 0) return;
        const anchor = cell.querySelector('a');
        if (!anchor) return;

        if (isNewVariant) {
          const tag = document.createElement('div');
          tag.className = 'featured_tag';
          tag.innerHTML = `<div class="slabel slabel-black">${badgeText}</div>`;
          anchor.prepend(tag);
          badgeShown = true;
        } else if (hasSticker) {
          // Restore sticker from inactive gallery for first image
          const inactiveSticker = this.inactiveGallery.querySelector('.sticker');
          if (inactiveSticker) {
            const clonedSticker = inactiveSticker.cloneNode(true);
            clonedSticker.style.display = '';
            anchor.prepend(clonedSticker);
          }
        }
      });
    }

    watchVideoActions() {
      this.wrapper.querySelectorAll(".gallery-cell video").forEach(video => {
        const parent = video.closest(".gallery-cell");
        const icon = parent.querySelector(".gicon");
        icon.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (video.paused) {
            video.play();
            icon.classList.add("gicon--pause");
            icon.classList.remove("gicon--play");
          } else {
            video.pause();
            icon.classList.remove("gicon--pause");
            icon.classList.add("gicon--play");
          }
        });
      });
    }

    copyAttributes(fromEl, toEl) {
      Array.from(fromEl.attributes).forEach(attr => {
        toEl.setAttribute(attr.name, attr.value);
      });
      Object.keys(fromEl.dataset).forEach(key => {
        toEl.dataset[key] = fromEl.dataset[key];
      });
    }

    destroy() {
      if (this.flickityInstance) {
        this.flickityInstance.off("select", this._onFlickitySelect);
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }
      if (this.thumbsEl) {
        this.thumbsEl.removeEventListener("scroll", this._onThumbsScroll);
      }
      window.removeEventListener("resize", this._onResize);
    }
  };

  // Static initializer for all containers
  ns.jsSmartGallery = {
    controllers: [],
    initAll() {
      this.controllers = [];
      document.querySelectorAll(".smart-gallery-container").forEach(wrapper => {
        const instance = new ns.SmartGallery(wrapper);
        instance.init();
        this.controllers.push(instance);
      });
    }
  };

  document.addEventListener("DOMContentLoaded", () => {
    ns.jsSmartGallery.initAll();
  });

})(window.PXUTheme);